"use client";

import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import {
  Ticket,
  TicketMessage,
  CreateTicketMessageDto,
} from "@/types/models/ticket";
import {
  Send,
  Paperclip,
  Clock,
  Shield,
  MessageCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import { format } from "date-fns";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchTicketMessagesRequest,
  createTicketMessageRequest,
  resetMessageFormState,
} from "@/store/slices/ticket.slice";
import { toast } from "sonner";

interface TicketCommunicationProps {
  ticket: Ticket;
}

export const TicketCommunication: React.FC<TicketCommunicationProps> = ({
  ticket,
}) => {
  const dispatch = useAppDispatch();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [newMessage, setNewMessage] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const currentUserId = useAppSelector(
    (state) => state.auth.adminAuth?.userId || state.auth.userId || "",
  );
  const { messages, messagesLoading, messageFormState } = useAppSelector(
    (state) => state.tickets,
  );

  useEffect(() => {
    if (ticket.id) {
      dispatch(fetchTicketMessagesRequest({ ticketId: ticket.id }));
    }
  }, [ticket.id, dispatch]);

  useEffect(() => {
    if (messageFormState.success) {
      setNewMessage("");
      setIsPrivate(false);
      dispatch(resetMessageFormState());
      toast.success("Comment sent successfully");
    }
  }, [messageFormState.success, dispatch]);

  useEffect(() => {
    if (messageFormState.error) {
      toast.error(messageFormState.error.message || "Failed to send comment");
    }
  }, [messageFormState.error]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const messageData: CreateTicketMessageDto = {
      ticketId: ticket.id,
      message: newMessage.trim(),
      isPrivate,
    };

    dispatch(createTicketMessageRequest(messageData));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const MessageCard = ({ message }: { message: TicketMessage }) => {
    const isOwnMessage = !!currentUserId && message.senderId === currentUserId;
    const senderLabel =
      message.senderName ||
      message.senderType?.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) ||
      "User";
    const displayName = isOwnMessage ? "You" : senderLabel;
    const avatarLabel = senderLabel;
    const senderRoleLabel =
      message.senderType ||
      message.senderRole?.toLowerCase() ||
      "admin";
    const formattedTime = format(
      new Date(message.created_at),
      "MMM dd, yyyy 'at' HH:mm",
    );

    return (
      <div
        className={`flex gap-3 ${isOwnMessage ? "flex-row-reverse" : "flex-row"} mb-4`}
      >
        <Avatar name={avatarLabel} className="h-10 w-10 flex-shrink-0" />

        <div
          className={`flex-1 max-w-[70%] ${isOwnMessage ? "items-end" : "items-start"} flex flex-col`}
        >
          <div
            className={`rounded-2xl px-4 py-3 ${
              isOwnMessage ? "bg-primary text-primary-foreground" : "bg-muted"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`text-sm font-semibold ${
                  isOwnMessage ? "text-primary-foreground" : "text-foreground"
                }`}
              >
                {displayName}
              </span>
              <Badge
                variant="outline"
                className={`text-xs ${
                  senderRoleLabel === "customer"
                    ? "bg-blue-100 text-blue-800 border-blue-200"
                    : senderRoleLabel === "admin"
                      ? "bg-purple-100 text-purple-800 border-purple-200"
                      : "bg-gray-100 text-gray-800 border-gray-200"
                } ${isOwnMessage ? "bg-opacity-20 text-primary-foreground border-primary-foreground/30" : ""}`}
              >
                {senderRoleLabel}
              </Badge>
              {message.isPrivate && (
                <Badge
                  variant="outline"
                  className={`text-xs flex items-center gap-1 ${
                    isOwnMessage
                      ? "bg-opacity-20 text-primary-foreground border-primary-foreground/30"
                      : "bg-orange-100 text-orange-800 border-orange-200"
                  }`}
                >
                  <Shield className="h-3 w-3" />
                  Private
                </Badge>
              )}
            </div>

            <div
              className={`text-sm leading-relaxed ${
                isOwnMessage ? "text-primary-foreground" : "text-foreground"
              }`}
            >
              {message.message}
            </div>

            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-3 space-y-1">
                {message.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 text-xs ${
                      isOwnMessage
                        ? "text-primary-foreground/80"
                        : "text-muted-foreground"
                    }`}
                  >
                    <Paperclip className="h-3 w-3" />
                    <span>{attachment}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className={`flex items-center gap-2 mt-1 text-xs text-muted-foreground ${
              isOwnMessage ? "flex-row-reverse" : "flex-row"
            }`}
          >
            <Clock className="h-3 w-3" />
            <span>{formattedTime}</span>
            <span>•</span>
            <span>{message.senderEmail || "No email"}</span>
            {message.senderPhone && (
              <>
                <span>•</span>
                <span>{message.senderPhone}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col h-full">
      {/* Header */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <MessageCircle className="h-6 w-6" />
            {ticket.subject || ticket.title || "Untitled"}
          </CardTitle>
          <div className="text-sm text-muted-foreground">
            {ticket.description}
          </div>
        </CardHeader>
      </Card>

      {/* Messages Container */}
      <Card className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Comments ({ticket.messageCount})
            </span>
            {(ticket.unreadCount || 0) > 0 && (
              <Badge variant="destructive" className="text-xs">
                {ticket.unreadCount || 0} unread
              </Badge>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="flex-1 min-h-0 flex flex-col overflow-hidden p-0">
          {/* Messages List */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
            {messagesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-muted-foreground">Loading comments...</div>
              </div>
            ) : messages && messages.length > 0 ? (
              <div className="space-y-4">
                {messages.map((message) => (
                  <MessageCard key={message.id} message={message} />
                ))}
                <div ref={messagesEndRef} />
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                No comments yet. Start the conversation!
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="shrink-0 border-t bg-background px-4 py-3">
            <div className="flex items-center gap-3">
              <Textarea
                placeholder={
                    isPrivate
                      ? "Add a private note..."
                      : "Type your comment..."
                }
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                className="min-h-10 h-10 resize-none py-2 px-3 flex-1"
              />

              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  Private note
                </span>
                <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
                {isPrivate ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </div>

              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || messageFormState.loading}
                className="flex items-center gap-2 shrink-0"
              >
                <Send className="h-3 w-3" />
                <span className="hidden sm:inline">
                  {messageFormState.loading ? "Sending..." : "Send"}
                </span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
